import { useParams } from '@tanstack/react-router';
import type { JSX } from 'react';
import { Page } from '@/components/layout/page.js';
import { ErrorState } from '@/components/ui/error-state.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs.js';
import { ApiError } from '@/lib/api/client.js';
import { DetailHeaderSkeleton, DetailPanelSkeleton } from './detail-header-skeleton.js';
import { OrderBlockedBanner } from './order-blocked-banner.js';
import { OrderHeader } from './order-header.js';
import { CsvTab } from './tabs/csv-tab.js';
import { FilesTab } from './tabs/files-tab.js';
import { LinesTab } from './tabs/lines-tab.js';
import { SummaryTab } from './tabs/summary-tab.js';
import { TimelineTab } from './tabs/timeline-tab.js';
import { useOrderDetail } from './use-orders-queries.js';

function readOrderId(params: unknown): string | undefined {
  if (typeof params !== 'object' || params === null) {
    return undefined;
  }
  const value = (params as Record<string, unknown>).id;
  return typeof value === 'string' ? value : undefined;
}

/** Squelette à la forme du détail : en-tête, onglets, deux adresses côte à côte puis un panneau. */
function DetailSkeleton(): JSX.Element {
  return (
    <Page width="detail">
      <DetailHeaderSkeleton />
      <div className="flex flex-col gap-6">
        <div className="flex h-10 items-end gap-4 border-b border-border pb-2.5">
          {['w-16', 'w-14'].map((width, index) => (
            <Skeleton key={`tab-${String(index)}`} className={`h-4 ${width}`} />
          ))}
        </div>
        <div className="grid gap-6 lg:grid-cols-2">
          <DetailPanelSkeleton lines={4} />
          <DetailPanelSkeleton lines={4} />
        </div>
        <DetailPanelSkeleton />
      </div>
    </Page>
  );
}

/**
 * Écran Détail commande (`02` §4). Route associée : `routes/commande-detail.tsx`
 * (`orderDetailRoute`, `path: '/commandes/$id'`). Lit l'identifiant via
 * `useParams({strict:false})` — générique, comme `useOriginFilter`
 * (`README-shell.md`) — pour ne pas dépendre de l'objet de route lui-même
 * (évite un import circulaire avec `routes/commande-detail.tsx`, qui importe
 * ce composant).
 */
export function OrderDetailPage(): JSX.Element {
  const rawParams: unknown = useParams({ strict: false });
  const id = readOrderId(rawParams);

  const detailQuery = useOrderDetail(id);

  if (!id) {
    return (
      <Page width="detail">
        <ErrorState message="Identifiant de commande manquant." />
      </Page>
    );
  }

  if (detailQuery.isPending) {
    return <DetailSkeleton />;
  }

  if (detailQuery.isError) {
    return (
      <Page width="detail">
        <ErrorState
          message={
            detailQuery.error instanceof ApiError
              ? detailQuery.error.message
              : 'Impossible de charger cette commande.'
          }
          detail={detailQuery.error instanceof ApiError ? detailQuery.error.detail : undefined}
          onRetry={() => void detailQuery.refetch()}
        />
      </Page>
    );
  }

  const order = detailQuery.data;

  return (
    <Page width="detail">
      <OrderHeader order={order} />
      <OrderBlockedBanner order={order} />

      <Tabs defaultValue="summary">
        <TabsList>
          <TabsTrigger value="summary">Résumé</TabsTrigger>
          <TabsTrigger value="lines">Lignes</TabsTrigger>
          <TabsTrigger value="csv">Aperçu CSV</TabsTrigger>
        </TabsList>
        <TabsContent value="summary">
          <SummaryTab order={order} />
          <div className="mt-6"><TimelineTab order={order} /></div>
        </TabsContent>
        <TabsContent value="lines">
          <LinesTab order={order} />
        </TabsContent>
        <TabsContent value="csv">
          <CsvTab orderId={id} order={order} />
          {order.fileIds.length > 1 ? <div className="mt-6"><FilesTab orderId={id} /></div> : null}
        </TabsContent>
      </Tabs>
    </Page>
  );
}
